import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Users, Percent, Store, RotateCcw, Loader2, Search, Printer } from "lucide-react";
import IntakeVoucher from "@/components/IntakeVoucher";

const channels = [
  { key: "mediator", label: "Mediator / Broker", icon: Users },
  { key: "commission", label: "Commission Basis", icon: Percent },
  { key: "direct", label: "Direct A1 Cars Buy", icon: Store },
  { key: "repeat", label: "Repeat Old Car", icon: RotateCcw },
];

const empty = {
  channel: "mediator",
  reg_no: "",
  model: "",
  year: "",
  color: "",
  office_purchase_rate: "",
  party_name: "",
  party_mobile: "",
  party_city: "",
  mediator_id: "",
  mediator_commission: "",
  customer_expected_price: "",
  agency_margin: "",
  commission_type: "fixed",
  purchase_price: "",
  total_buying_cost: "",
  paid_cash: "",
  paid_bank: "",
  notes: "",
};

// Module-scope so React keeps a stable component type (prevents input focus loss)
function Field({ label, testid, value, onChange, type = "text", placeholder, prefix, mobile }) {
  const [touched, setTouched] = useState(false);
  const invalid = mobile && touched && value && !/^\d{10}$/.test(value);
  const handle = (e) => {
    if (mobile) {
      const v = e.target.value.replace(/\D/g, "").slice(0, 10);
      onChange({ target: { value: v } });
    } else onChange(e);
  };
  return (
    <div>
      <label className="a1-label">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-slate-400">{prefix}</span>}
        <input
          data-testid={testid}
          type={mobile ? "tel" : type}
          inputMode={mobile ? "numeric" : undefined}
          maxLength={mobile ? 10 : undefined}
          className={`a1-input${invalid ? " border-red-500" : ""}`}
          style={prefix ? { paddingLeft: "1.75rem" } : {}}
          value={value}
          placeholder={placeholder}
          onBlur={mobile ? () => setTouched(true) : undefined}
          onChange={handle}
        />
      </div>
      {invalid && <p className="text-red-500 text-xs font-semibold mt-1">Please enter a valid 10-digit mobile number</p>}
    </div>
  );
}

export default function Intake() {
  const [form, setForm] = useState(empty);
  const [mediators, setMediators] = useState([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [voucher, setVoucher] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/mediators").then((r) => setMediators(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const on = (k) => (e) => set(k, e.target.value);
  const num = (v) => (v === "" || v === null ? 0 : parseFloat(v));

  const balanceHold = num(form.total_buying_cost) - (num(form.paid_cash) + num(form.paid_bank));

  const lookupRepeat = async () => {
    if (!form.reg_no.trim()) return;
    try {
      const { data } = await api.get(`/vehicles/repeat/${encodeURIComponent(form.reg_no.trim())}`);
      setHistory(data);
      if (data.length) {
        const last = data[0];
        setForm((f) => ({ ...f, model: last.model, year: last.year, color: last.color, party_name: last.party_name }));
        toast.success(`Found ${data.length} past record(s) for this car`);
      } else {
        toast.info("No previous history found for this registration");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const chooseMediator = (id) => {
    const m = mediators.find((x) => x.id === id);
    if (m) setForm((f) => ({ ...f, mediator_id: id, party_name: m.name, party_mobile: m.mobile, party_city: m.city }));
  };

  const buildPayload = () => ({
    ...form,
    office_purchase_rate: num(form.office_purchase_rate),
    customer_expected_price: num(form.customer_expected_price),
    agency_margin: num(form.agency_margin),
    mediator_commission: num(form.mediator_commission),
    purchase_price: num(form.purchase_price),
    total_buying_cost: num(form.total_buying_cost),
    paid_cash: num(form.paid_cash),
    paid_bank: num(form.paid_bank),
    amount_paid_now: num(form.paid_cash) + num(form.paid_bank),
    commission_type: form.commission_type,
    mediator_id: form.mediator_id || null,
  });

  const submit = async (e, printAfter = false) => {
    if (e) e.preventDefault();
    if (!form.reg_no.trim() || !form.model.trim()) {
      toast.error("Registration No and Model are required");
      return;
    }
    if (form.party_mobile && !/^\d{10}$/.test(form.party_mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles", buildPayload());
      toast.success("Car added to stock");
      if (printAfter) {
        setVoucher(data);
      } else {
        navigate(`/vehicle/${data.id}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="a1-fade max-w-5xl">
      <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight mb-1">New Vehicle Intake</h1>
      <p className="text-slate-500 font-semibold mb-6">Inward Date auto-set to today • {formatDate(new Date())}</p>

      {/* Channel tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {channels.map((ch) => (
          <button
            key={ch.key}
            data-testid={`channel-${ch.key}`}
            onClick={() => set("channel", ch.key)}
            className={`p-4 rounded-2xl border-2 text-left transition-colors ${
              form.channel === ch.key ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <ch.icon className="w-7 h-7 mb-2" strokeWidth={2.5} />
            <div className="font-bold text-sm leading-tight">{ch.label}</div>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Vehicle basics */}
        <div className="a1-card p-6">
          <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Vehicle Details</h3>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="a1-label">Registration Number</label>
              <div className="flex gap-2">
                <input
                  data-testid="intake-reg_no"
                  className="a1-input"
                  value={form.reg_no}
                  placeholder="MH12AB1234"
                  onChange={(e) => set("reg_no", e.target.value.toUpperCase())}
                />
                {form.channel === "repeat" && (
                  <button type="button" onClick={lookupRepeat} className="a1-btn a1-btn-primary shrink-0 !px-4" data-testid="repeat-lookup">
                    <Search className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Model Name" testid="intake-model" value={form.model} onChange={on("model")} placeholder="Maruti Swift VXI" />
            <Field label="Year" testid="intake-year" value={form.year} onChange={on("year")} placeholder="2019" />
            <Field label="Color" testid="intake-color" value={form.color} onChange={on("color")} placeholder="White" />
          </div>

          {form.channel === "repeat" && history.length > 0 && (
            <div className="mt-5 border-t border-slate-200 pt-4">
              <div className="text-sm font-bold text-slate-500 mb-3">Previous Ownership &amp; Sale History</div>
              <div className="space-y-3">
                {history.map((h) => (
                  <div key={h.id} className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-sm" data-testid={`repeat-history-${h.id}`}>
                    <div className="flex justify-between flex-wrap gap-2 mb-2">
                      <span className="font-head font-bold tracking-tight">{h.model} • {h.reg_no}</span>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white capitalize"
                        style={{ background: { available: "#16A34A", booked: "#D97706", sold: "#2563EB", delivered: "#475569" }[h.status] || "#475569" }}>
                        {h.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>
                        <div className="text-xs font-bold text-slate-400">Previous Buyer</div>
                        <div className="font-bold">{h.customer_name || "—"}</div>
                        <div className="num text-xs text-slate-500">{h.customer_mobile || "No mobile"}</div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-400">Sale Date &amp; Price</div>
                        <div className="num font-bold">{h.selling_price ? formatINR(h.selling_price) : "—"}</div>
                        <div className="num text-xs text-slate-500">{h.sold_at ? formatDate(h.sold_at) : (h.status === "sold" || h.status === "delivered" ? formatDate(h.inward_date) : "Not sold")}</div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-400">Purchase / Inward Rate</div>
                        <div className="num font-bold">{formatINR(h.office_purchase_rate || h.total_buying_cost || h.purchase_price)}</div>
                        <div className="num text-xs text-slate-500">Inward {formatDate(h.inward_date)}</div>
                      </div>
                    </div>
                    {(h.payments?.length > 0 || h.advances?.length > 0) && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <div className="text-xs font-bold text-slate-400 mb-1">Payment History</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-slate-600">
                          {h.advances?.filter((a) => a.direction === "in").map((a) => (
                            <span key={a.id} className="num">Advance {a.mode}: {formatINR(a.amount)}</span>
                          ))}
                          {h.payments?.map((p) => (
                            <span key={p.id} className="num">{p.mode.toUpperCase()}: {formatINR(p.amount)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Office purchase rate — hidden for Commission (showroom investment = ₹0) */}
        {form.channel !== "commission" && (
          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-1">Office Purchase Rate</h3>
            <p className="text-slate-500 font-semibold text-sm mb-4">Internal buy rate — visible to staff for quick reference.</p>
            <div className="max-w-xs">
              <Field label="Purchase Rate (₹)" testid="intake-office_purchase_rate" value={form.office_purchase_rate} onChange={on("office_purchase_rate")} type="number" prefix="₹" placeholder="900000" />
            </div>
          </div>
        )}

        {/* Channel specific */}
        {form.channel === "mediator" && (
          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Mediator / Broker</h3>
            {mediators.length > 0 && (
              <div className="mb-5">
                <label className="a1-label">Choose a saved mediator</label>
                <div className="flex gap-2 flex-wrap">
                  {mediators.map((m) => (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => chooseMediator(m.id)}
                      className={`px-4 py-2 rounded-full border-2 font-bold text-sm ${
                        form.mediator_id === m.id ? "border-[#EA580C] bg-[#EA580C] text-white" : "border-slate-200 bg-white"
                      }`}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Mediator Name" testid="intake-party_name" value={form.party_name} onChange={on("party_name")} placeholder="Full name" />
              <Field label="Mobile" testid="intake-party_mobile" value={form.party_mobile} onChange={on("party_mobile")} placeholder="10-digit mobile" mobile />
              <Field label="City" testid="intake-party_city" value={form.party_city} onChange={on("party_city")} placeholder="City" />
              <Field label="Brokerage / Commission (₹)" testid="intake-mediator_commission" value={form.mediator_commission} onChange={on("mediator_commission")} type="number" prefix="₹" placeholder="15000" />
            </div>
            <p className="text-xs text-slate-400 font-semibold mt-2">Brokerage is the fee the mediator earns for sourcing this car — tracked in their profile.</p>
          </div>
        )}

        {form.channel === "commission" && (
          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-1">Commission Basis</h3>
            <p className="text-slate-500 font-semibold text-sm mb-4">No showroom purchase — we only earn a commission.</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Seller Name" testid="intake-party_name" value={form.party_name} onChange={on("party_name")} placeholder="Owner name" />
              <Field label="Mobile" testid="intake-party_mobile" value={form.party_mobile} onChange={on("party_mobile")} placeholder="10-digit mobile" mobile />
              <Field label="Customer Expected Price" testid="intake-customer_expected_price" value={form.customer_expected_price} onChange={on("customer_expected_price")} type="number" prefix="₹" placeholder="800000" />
              <div>
                <label className="a1-label">Agency Commission</label>
                <div className="flex gap-2">
                  <div className="flex rounded-xl overflow-hidden border-2 border-slate-200 shrink-0">
                    <button type="button" data-testid="commission-fixed" onClick={() => set("commission_type", "fixed")}
                      className={`px-4 font-bold ${form.commission_type === "fixed" ? "bg-[#1E3A8A] text-white" : "bg-white text-slate-600"}`}>₹</button>
                    <button type="button" data-testid="commission-percent" onClick={() => set("commission_type", "percent")}
                      className={`px-4 font-bold ${form.commission_type === "percent" ? "bg-[#1E3A8A] text-white" : "bg-white text-slate-600"}`}>%</button>
                  </div>
                  <input data-testid="intake-agency_margin" type="number" className="a1-input"
                    value={form.agency_margin} onChange={on("agency_margin")}
                    placeholder={form.commission_type === "percent" ? "5" : "50000"} />
                </div>
              </div>
            </div>
          </div>
        )}

        {form.channel === "direct" && (
          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Direct A1 Cars Buy</h3>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Seller Name" testid="intake-party_name" value={form.party_name} onChange={on("party_name")} placeholder="Seller name" />
              <Field label="Seller Mobile" testid="intake-party_mobile" value={form.party_mobile} onChange={on("party_mobile")} placeholder="10-digit mobile" mobile />
              <Field label="Seller City" testid="intake-party_city" value={form.party_city} onChange={on("party_city")} placeholder="City" />
              <Field label="Outright Purchase Price" testid="intake-purchase_price" value={form.purchase_price} onChange={on("purchase_price")} type="number" prefix="₹" placeholder="850000" />
            </div>
          </div>
        )}

        {/* Payment split (mediator & direct) */}
        {(form.channel === "mediator" || form.channel === "direct") && (
          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Payment Split Matrix</h3>
            <div className="grid md:grid-cols-4 gap-5">
              <Field label="Total Agreed Buying Cost" testid="intake-total_buying_cost" value={form.total_buying_cost} onChange={on("total_buying_cost")} type="number" prefix="₹" placeholder="1000000" />
              <Field label="Paid Now — Cash" testid="intake-paid_cash" value={form.paid_cash} onChange={on("paid_cash")} type="number" prefix="₹" placeholder="500000" />
              <Field label="Paid Now — Bank" testid="intake-paid_bank" value={form.paid_bank} onChange={on("paid_bank")} type="number" prefix="₹" placeholder="400000" />
              <div>
                <label className="a1-label">Balance on Hold</label>
                <div className="a1-input flex items-center num font-black text-xl" style={{ background: "#FFF7ED", color: "#EA580C", borderColor: "#FED7AA" }}>
                  {formatINR(balanceHold > 0 ? balanceHold : 0)}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 font-semibold mt-2">Balance kept pending for RC transfer / thumb registration — settle later from the car page.</p>
          </div>
        )}

        <div>
          <label className="a1-label">Notes (optional)</label>
          <textarea data-testid="intake-notes" className="a1-input !h-auto py-3" rows={2} value={form.notes} onChange={on("notes")} />
        </div>

        <div className="flex gap-3 flex-wrap">
          <button type="button" data-testid="intake-save-print" disabled={busy} onClick={(e) => submit(e, true)} className="a1-btn a1-btn-action">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Printer className="w-5 h-5" /> Save &amp; Print Receipt</>}
          </button>
          <button type="button" onClick={() => setForm(empty)} className="a1-btn a1-btn-ghost">Clear</button>
        </div>
      </form>

      {voucher && (
        <IntakeVoucher
          vehicle={voucher}
          onClose={() => {
            const id = voucher.id;
            setVoucher(null);
            navigate(`/vehicle/${id}`);
          }}
        />
      )}
    </div>
  );
}
